import { translate } from "@/lib/i18n";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Header } from "@/components/Header";
import { SiteFooter } from "@/components/SiteFooter";
import { useLang } from "@/context/LangContext";

export const COMPANY = {
  name: "PIXAWEB Sagl", address: "Via Cantonale 19, 6900 Lugano, Svizzera", uid: "CHE-364.397.948",
  rc: "CH-501.4.024.588-7", form: "Società a garanzia limitata (Sagl)", capital: "CHF 20.000,00",
  email: "legal@pixawebch.com", notices: "Via Cantonale 19, 6900 Lugano (TI)",
};

const UI = {
  it: { back: "Torna al sito", toc: "Indice", updated: "Ultimo aggiornamento", note: "Il presente documento è una base informativa: per la piena conformità legale è consigliata la verifica da parte di un consulente legale svizzero esperto in LPD e diritto digitale." },
  en: { back: "Back to site", toc: "Contents", updated: "Last updated", note: "This document is provided for information: for full legal compliance, review by a Swiss legal advisor experienced in FADP and digital law is recommended. In case of discrepancy, the Italian version prevails." },
  de: { back: "Zurück zur Website", toc: "Inhalt", updated: "Zuletzt aktualisiert", note: "Dieses Dokument dient der Information: Für die vollständige Rechtskonformität wird eine Prüfung durch einen Schweizer Rechtsberater mit Erfahrung im DSG und im digitalen Recht empfohlen. Bei Abweichungen ist die italienische Fassung massgebend." },
  fr: { back: "Retour au site", toc: "Sommaire", updated: "Dernière mise à jour", note: "Ce document est fourni à titre informatif : pour une conformité juridique complète, une vérification par un conseiller juridique suisse expérimenté en LPD et en droit numérique est recommandée. En cas de divergence, la version italienne fait foi." },
  es: { back: "Volver al sitio", toc: "Índice", updated: "Última actualización", note: "Este documento tiene carácter informativo: para el pleno cumplimiento legal se recomienda la revisión por parte de un asesor jurídico suizo experto en LPD y derecho digital. En caso de discrepancia, prevalece la versión italiana." },
};

const tx = (lang, v) => (typeof v === "string" ? translate(lang, v) : { list: v.list.map((x) => translate(lang, x)) });
const localize = (c, lang) => ({ title: translate(lang, c.title), updated: translate(lang, c.updated), intro: translate(lang, c.intro),
  sections: c.sections.map((s) => ({ title: translate(lang, s.title), body: s.body.map((b) => tx(lang, b)) })) });

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Body = ({ b }) => (typeof b === "string" ? <p>{b}</p> : (
  <ul className="list-disc pl-5 space-y-1.5">{b.list.map((li) => <li key={li}>{li}</li>)}</ul>
));

// content: { it: {title, updated, intro, sections}, en: {...} }
export const LegalPage = ({ content, testid }) => {
  const { lang, setLang, t } = useLang();
  const { title, updated, intro, sections } = content[lang] || localize(content.it, lang);
  const ui = UI[lang];
  return (
    <div className="min-h-screen bg-[#E6EAEC] flex flex-col">
      <Header lang={lang} setLang={setLang} t={t} />
      <main data-testid={testid} className="max-w-6xl mx-auto w-full px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        <aside className="lg:sticky lg:top-24 self-start">
          <Link to="/" data-testid="legal-back" className="inline-flex items-center gap-1.5 text-sm text-[#5b6168] hover:text-[#E0262C] mb-6">
            <ArrowLeft className="w-4 h-4" /> {ui.back}
          </Link>
          <nav data-testid="legal-toc" className="border border-[#cfd6d9] bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest font-mono text-[#71717a] mb-3">{ui.toc}</div>
            <ol className="space-y-1.5 text-sm">
              {sections.map((s, i) => (
                <li key={s.title}><a href={`#${slug(s.title)}`} className="text-[#2B3036] hover:text-[#E0262C] hover:underline">{i + 1}. {s.title}</a></li>
              ))}
            </ol>
          </nav>
        </aside>
        <article className="bg-white border border-[#cfd6d9] p-8 sm:p-12">
          <div className="text-[10px] uppercase tracking-widest font-mono text-[#E0262C] mb-3">{COMPANY.name}</div>
          <h1 data-testid="legal-title" className="font-display text-4xl sm:text-5xl mb-3">{title}</h1>
          <p className="text-xs font-mono text-[#71717a] mb-8">{ui.updated}: {updated}</p>
          <div className="max-w-[68ch] space-y-10 text-[15px] leading-relaxed text-[#2B3036]">
            {intro && <p className="text-[#5b6168]">{intro}</p>}
            {sections.map((s, i) => (
              <section key={s.title} id={slug(s.title)} className="scroll-mt-24">
                <h2 className="font-display text-xl mb-3">{i + 1}. {s.title}</h2>
                <div className="space-y-3">{s.body.map((b, j) => <Body key={j} b={b} />)}</div>
              </section>
            ))}
            <p className="text-xs text-[#71717a] border-t border-[#e4e4e7] pt-6">{ui.note}</p>
          </div>
        </article>
      </main>
      <div className="mt-auto"><SiteFooter /></div>
    </div>
  );
};
